import {
  ErrorModal,
  MesfModal,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Checkbox, Grid, TextField, Typography } from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { get } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { modalStyles } from "@/shared/styles/modalStyles";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { PourbackReasonAutocomplete } from "../../../../../../shared/autocompletes/PourbackReasonAutocomplete";
import { getPourbackReasons } from "../../../../../../shared/repositories/PourbackReasonRepository";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useCasterDataEntryActions } from "../../../hooks/useCasterDataEntryActions";
import { Process } from "../../../models/caster.models";
import { CasterPourbackReason } from "../../../models/catalogs.models";
import { HeatDataEntryType } from "@/pages/meltshop/heats/models/HeatDataEntryType";
import { CommentsField } from "@/shared/fields/CommentsField";

interface Props {
  show: boolean;
  onHide: (shouldUpdate?: boolean) => void;
  process?: Process;
  heat?: HeatDataEntryType | null;
  onSubmit?: (data: FormData, pourbackId?: number | null) => Promise<void>;
}

export interface FormData {
  HeatName: string;
  PourbackWeight: number | null;
  PourbackReasonId: number | null;
  PourbackReasonName: string | null;
  PourbackComments?: string | null;
  IsFinished?: boolean;
}

export const PourbackModal = ({
  show,
  onHide,
  process,
  heat,
  onSubmit: externalOnSubmit,
}: Props) => {
  const [isInitiallyFinished, setIsInitiallyFinished] =
    useState<boolean>(false);
  const [pourbackId, setPourbackId] = useState<number | null>(null);
  const [reasons, setReasons] = useState<CasterPourbackReason[]>([]);

  const {
    state: { settings },
  } = useUTLSettingsContext();
  const MIN_POURBACK_WEIGHT = get(settings, "HEATS_MIN_POURBACK_WEIGHT", 1);
  const MAX_POURBACK_WEIGHT = get(settings, "HEATS_MAX_POURBACK_WEIGHT", 200);

  const {
    state: { errorMessage, isSubmitLoading },
    actions: { setErrorMessage, setIsSubmitLoading },
  } = useDataEntryContext();
  const { savePourback } = useCasterDataEntryActions();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      PourbackWeight: null,
      PourbackReasonId: null,
      PourbackReasonName: null,
    },
  });

  const defaultOnSubmit: SubmitHandler<FormData> = async (data) => {
    setIsSubmitLoading(true);
    try {
      await savePourback({
        PourbackReasonId: data.PourbackReasonId!,
        PourbackWeight: data.PourbackWeight!,
        PourbackReasonName: data.PourbackReasonName!,
      });
      onHide(true);
    } catch (error) {
      setErrorMessage("Failed to save pourback: " + error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleFormSubmit: SubmitHandler<FormData> = async (data) => {
    if (externalOnSubmit) {
      setIsSubmitLoading(true);
      try {
        await externalOnSubmit(data, heat?.PourbackId);
        onHide(true);
      } catch (error) {
        setErrorMessage("Failed to submit: " + error);
      } finally {
        setIsSubmitLoading(false);
      }
    } else {
      await defaultOnSubmit(data);
    }
  };

  const getReasons = useCallback(async () => {
    const response = await getPourbackReasons();
    if (response.ok) {
      const reasonsData = response?.data?.tables[0].rows || [];
      setReasons(reasonsData);
    } else {
      setErrorMessage(response.message);
    }
  }, [setErrorMessage]);

  useEffect(() => {
    if (show) {
      setPourbackId(null);
      getReasons();
    }
  }, [show, heat?.HeatId, getReasons]);

  useEffect(() => {
    if (show && !isSubmitLoading && reasons.length > 0) {
      let selectedReasonId = null;
      let selectedReasonName = null;

      if (heat?.PourbackReasonName) {
        const selectedReason = reasons.find(
          (reason) => reason.PourbackReasonName === heat.PourbackReasonName,
        );
        selectedReasonId = selectedReason?.PourbackReasonId || null;
        selectedReasonName = selectedReason?.PourbackReasonName || null;
      } else if (process?.PourbackReasonName) {
        const selectedReason = reasons.find(
          (reason) => reason.PourbackReasonName === process.PourbackReasonName,
        );
        selectedReasonId = selectedReason?.PourbackReasonId || null;
        selectedReasonName = selectedReason?.PourbackReasonName || null;
      }

      if (!selectedReasonId) {
        const unknownReason = reasons.find(
          (reason) => reason.PourbackReasonId === 1,
        );
        if (unknownReason) {
          selectedReasonId = unknownReason.PourbackReasonId;
          selectedReasonName = unknownReason.PourbackReasonName;
        }
      }

      const initialFinishedValue = heat?.HeatStatusCode === "F" || false;
      setIsInitiallyFinished(initialFinishedValue);

      const resetValues = {
        PourbackWeight: heat?.PourbackWeight || null,
        PourbackReasonId: selectedReasonId,
        PourbackReasonName: selectedReasonName,
        PourbackComments: heat?.PourbackComments || "",
        HeatName: heat?.HeatName || "",
        IsFinished: initialFinishedValue,
      };
      reset(resetValues);
    }
  }, [show, reasons, process, reset, heat, isSubmitLoading]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            title="Pourback Declaration"
            open={show}
            handleClose={() => onHide()}
            id="declare-pourback-modal"
            maxWidth="md"
          >
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <MesfModal.Content dividers sx={modalStyles.modalContent}>
                <Grid container spacing={2}>
                  {heat && (
                    <Grid item xs={12} md={12}>
                      <Controller
                        name="HeatName"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Heat Name"
                            {...field}
                            value={field.value || ""}
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={12}>
                    <Controller
                      name="PourbackWeight"
                      control={control}
                      rules={{
                        required: "Weight is required",
                        min: {
                          value: MIN_POURBACK_WEIGHT,
                          message: `Minimum Weight is ${MIN_POURBACK_WEIGHT}`,
                        },
                        max: {
                          value: MAX_POURBACK_WEIGHT,
                          message: `Maximum Weight is ${MAX_POURBACK_WEIGHT}`,
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ""}
                          type="number"
                          label="Weight [tons]"
                          variant="outlined"
                          size="small"
                          error={!!errors.PourbackWeight}
                          helperText={errors.PourbackWeight?.message}
                          onFocus={(e) => e.target.select()}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Controller
                      name="PourbackReasonId"
                      control={control}
                      rules={{ required: "Reason is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <PourbackReasonAutocomplete
                          label="Reason"
                          value={field.value || null}
                          onChange={(reasonId: number) => {
                            const selectedReason = reasons.find(
                              (r) => r.PourbackReasonId === reasonId,
                            );
                            field.onChange(reasonId);
                            setValue(
                              "PourbackReasonName",
                              selectedReason?.PourbackReasonName || null,
                            );
                          }}
                          fieldError={error}
                        />
                      )}
                    />
                  </Grid>
                  {heat && (
                    <Grid item xs={12} md={12}>
                      <Controller
                        name="PourbackComments"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <CommentsField
                            value={field.value ?? ""}
                            onChange={(val: string) => {
                              field.onChange(val);
                            }}
                            fieldError={error}
                            getValues={getValues}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {heat && (
                    <Grid item xs={12} md={12}>
                      <Controller
                        name="IsFinished"
                        control={control}
                        render={({ field }) => (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={!!field.value}
                                  onChange={(e) =>
                                    field.onChange(e.target.checked)
                                  }
                                  disabled={
                                    !heat?.EnableUnfinishedPourback ||
                                    heat?.HeatStatusCode === "C" ||
                                    heat?.HeatStatusCode === "O" ||
                                    heat?.HeatStatusCode === "P"
                                  }
                                />
                              }
                              label="Finish Heat"
                            />
                            {(heat?.HeatStatusCode === "C" ||
                              heat?.HeatStatusCode === "O" ||
                              heat?.HeatStatusCode === "P") && (
                              <Typography
                                variant="caption"
                                color="error"
                                display="block"
                                style={{ marginTop: 4 }}
                              >
                                Heat already accepted at Caster. To change the
                                status to 'Finish', please use the Caster Data
                                Entry page
                              </Typography>
                            )}
                          </>
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </MesfModal.Content>
              <MesfModal.Actions sx={modalStyles.modalActions}>
                <FooterModalButtons
                  isSubmitLoading={isSubmitLoading}
                  onHide={onHide}
                />
              </MesfModal.Actions>
            </form>
          </MesfModal>
        </Grid>
      </Grid>
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Error declaring pourback"
      />
    </>
  );
};
